/**
 * Persistent identity keys for the web client (spec 200, Fase 3).
 *
 * After a successful pair, the web stores the unwrapped credentials
 * (teamKey, companyToken, technicianId, name, role, expiresAt) so a
 * page reload doesn't force a re-pair. Storage lives in IndexedDB
 * via the `meta` object store.
 *
 * MVP threat model (spec 200 §4.3):
 *   The values are stored cleartext. IndexedDB is sandboxed per-origin,
 *   so anyone executing JS on `app.solvr.es` already has the ambient
 *   teamKey via the running session. PIN-encrypting these with
 *   WebCrypto + a user-chosen PIN is queued for Fase 2 of spec 200.
 *
 * Why a separate `meta` store and not the `works`/`photos` stores
 * created in Fase 2:
 *   `meta` is keyed by string (`'identity'`, `'pairing'`, future
 *   `'theme'`, etc.) rather than by an object's `id` field. Mixing
 *   it with `works` would mean adding a synthetic `id` to every
 *   identity blob and writing the blob through saveWork. This way
 *   the surface stays small and explicit.
 */

// Spec 200 Fase 8 — the identity registry lives in a separate
// IndexedDB instance from the works/photos/voiceNotes data. Both
// modules used to open the same physical database (`cuadrilla`)
// with different version numbers, which raised
// `The requested version (1) is less than the existing version (2).`
// the moment one path beat the other to `indexedDB.open()`.
//
// Schema migration policy (project-wide):
//   Bump `DB_VERSION` and add a branch in `onupgradeneeded` that
//   PRESERVES the existing rows for every schema change. The
//   one-shot migration from the legacy single-DB to this split
//   layout lives in `migration.ts` and runs on every app boot
//   (idempotent via a localStorage sentinel). **Never break BC.
//   Never ask the user to clear site data.**
const DB_NAME = 'cuadrilla-identity';
const DB_VERSION = 1;
const STORE_META = 'meta';

// Single-row keys for the `meta` store. Keep the literal strings
// stable — changing them requires a real migration.
export const META_KEY_IDENTITY = 'identity';

/**
 * Identity record persisted after a successful pairing. Mirrors the
 * payload shape mobile encrypts in `ConnectWebScreen.tsx`.
 */
export interface WebIdentity {
  /** Hex-encoded 32-byte team key (the same teamKey mobile holds). */
  teamKey: string;
  companyId: string;
  technicianId: string;
  name: string;
  isAdmin: boolean;
  role: 'creator' | 'admin' | 'member';
  /** SHA256(teamKey) — what the relay's HTTP endpoints + WS identify
   *  expect in `x-company-token` / the `companyToken` field. */
  companyToken: string;
  /** Epoch ms when the identity expires. The web SHOULD revalidate
   *  via a fresh pair when this elapses. Default 30 days from pair. */
  expiresAt: number;
  /** When the row was first persisted (for diagnostic purposes). */
  pairedAt: number;
  /** Locally-generated id of THIS browser session, surfaced in the
   *  web_terminals table so the mobile-side revoke list can show
   *  per-browser entries. Stable across reloads of the same browser
   *  while the IndexedDB row survives. */
  terminalId: string;
}

// ── DB connection ───────────────────────────────────────────────────

let _identityCache: WebIdentity | null = null;

function _open(): Promise<IDBDatabase> {
  return new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      // keyPath: 'k' so we can `.put({ k: 'identity', v: <blob> })`
      // and the schema stays generic for future single-row keys
      // without bumping the version.
      if (!db.objectStoreNames.contains(STORE_META)) {
        db.createObjectStore(STORE_META, { keyPath: 'k' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('indexedDB open failed'));
    req.onblocked = () => reject(new Error('indexedDB open blocked — close other tabs and reload'));
  });
}

// ── Identity get/set/clear ──────────────────────────────────────────

/**
 * Persist the identity record. Called by `Pair.tsx` after the pair
 * payload decrypts cleanly. Replaces any prior identity (re-pair on
 * the same browser is a write, not an upsert error).
 */
export async function saveIdentity(identity: WebIdentity): Promise<void> {
  const db = await _open();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_META, 'readwrite');
    tx.objectStore(STORE_META).put({ k: META_KEY_IDENTITY, v: identity });
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('saveIdentity tx error'));
  });
  _identityCache = identity;
}

/**
 * Read the persisted identity. Returns null when no row exists, or
 * when the row exists but `expiresAt` has elapsed (the web should
 * treat lapsed identities as "needs re-pair").
 */
export async function getIdentity(): Promise<WebIdentity | null> {
  if (_identityCache) {
    if (_identityCache.expiresAt > Date.now()) return _identityCache;
    // Expired: clear in-memory cache; persistent row stays around
    // for the audit (and `clearIdentity()` on revoke handles deletion).
    _identityCache = null;
  }
  const db = await _open();
  return new Promise<WebIdentity | null>((resolve, reject) => {
    const tx = db.transaction(STORE_META, 'readonly');
    const req = tx.objectStore(STORE_META).get(META_KEY_IDENTITY);
    req.onsuccess = () => {
      const row = req.result as { v: WebIdentity } | undefined;
      if (!row) return resolve(null);
      if (row.v.expiresAt <= Date.now()) {
        // Lapsed — caller treats null as "needs re-pair".
        return resolve(null);
      }
      _identityCache = row.v;
      resolve(row.v);
    };
    req.onerror = () => reject(req.error ?? new Error('getIdentity req error'));
  });
}

/**
 * Wipe the identity row. Called on:
 *   - explicit user logout from Settings
 *   - 401 from the relay on any authenticated request (a revoked
 *     terminal — the web detects revocation by losing auth)
 *   - sync `wipe` message from another device
 *
 * The works/photos/voiceNotes stores are wiped separately by
 * `db.ts#clearAll()` — we keep them apart so a "logout but keep
 * cached works for the next pair" UX is at least possible later.
 */
export async function clearIdentity(): Promise<void> {
  _identityCache = null;
  const db = await _open();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_META, 'readwrite');
    tx.objectStore(STORE_META).delete(META_KEY_IDENTITY);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('clearIdentity tx error'));
  });
}

/** Test helper — drop the in-memory cache without touching IndexedDB. */
export function _resetIdentityCache(): void {
  _identityCache = null;
}
